import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { nodeMetaDataJsonSchema7, SFNodeType } from '@sciflow/schema';
import { selectTemplate } from 'export';
import { NodeMetaEditorDialog } from 'projects/ui-components/src';
import { BehaviorSubject, combineLatest, firstValueFrom, map, filter } from 'rxjs';
import { selectFiles } from '../../../editor.reducer';
import { FigurePreviewModalComponent } from '../figure-preview-modal/figure-preview-modal.component';
import { updateMarkAttrs } from '../../../prosemirror/commands';
import { EditorView } from 'prosemirror-view';

type MetadataMarkContext = {
  type: string;
  attrs: Record<string, any>;
  id?: string | null;
  from?: number;
  to?: number;
};

@Component({
  selector: 'sf-figure-menu',
  templateUrl: './figure-menu.component.html',
  styleUrls: ['./figure-menu.component.scss'],
  standalone: false,
})
export class FigureMenuComponent implements OnInit {
  private figureNodeSubject$ = new BehaviorSubject<{ [key: string]: any } | null>(null);
  private markContext: MetadataMarkContext | null = null;

  figureNode: { [key: string]: any } | null = null;
  mode: 'figure' | 'node' | 'mark' = 'node';
  @Input() set node(value: any) {
    this.figureNode = value;
    this.markContext = null;
    if (value) {
      const typeName = value?.type?.name ?? value?.attrs?.type;
      this.mode = typeName === SFNodeType.figure ? 'figure' : 'node';
    } else {
      this.mode = 'node';
    }
    this.figureNodeSubject$.next(this.mode === 'figure' ? value : null);
  }

  @Input() set mark(value: MetadataMarkContext | null) {
    this.markContext = value && value.type ? value : null;
    if (this.markContext) {
      this.figureNode = null;
      this.figureNodeSubject$.next(null);
      this.mode = 'mark';
    }
  }

  @Input() editorView?: EditorView;
  @Input('document-id') documentId;
  @Input() isUploading = false;
  @Input() isDropTarget = false;
  @Input() captionData;
  @Input() file;
  @Input() label?: string;
  @Input() tooltip?: string;

  @ViewChild('fileUpload', { static: true }) fileUploadButton: ElementRef;

  @Output() commands = new EventEmitter();

  emit(command: { id: string; payload?: any }) {
    this.commands.emit(command);
  }

  matchingFile$ = combineLatest([
    this.store.select(selectFiles),
    this.figureNodeSubject$
  ]).pipe(
    filter(([files, figureNode]) => !!files && !!figureNode),
    map(([files, figureNode]) => {
      const fileId = figureNode?.attrs?.id;
      if (!fileId) return null;
      
      return files.find((file) => file.id === fileId) || null;
    }),
  );

  constructor(
    public dialog: MatDialog,
    private store: Store,
  ) {}

  get buttonLabel(): string {
    if (this.label) {
      return this.label;
    }

    if (this.mode === 'mark') {
      const raw = this.markContext?.attrs?.entries?.[0]?.raw || this.markContext?.type;
      return raw ? `Edit ${raw}` : 'Edit index entry';
    }

    if (this.figureNode?.attrs?.type) {
      return `Edit ${this.figureNode.attrs.type}`;
    }

    return 'Edit metadata';
  }

  get buttonTooltip(): string {
    if (this.tooltip) {
      return this.tooltip;
    }

    if (this.mode === 'mark') {
      return 'Edit index entry';
    }

    return 'Edit metadata';
  }

  delete() {
    this.emit({ id: 'delete' });
  }

  addCaption() {
    this.emit({ id: 'add-caption' });
  }

  uploadImage() {
    this.fileUploadButton.nativeElement.click();
  }

  async onUpload(event) {
    const files = (event.srcElement || event.target).files;
    this.isUploading = true;
    // delete this.figureNode.attrs.src;

    this.emit({ id: 'upload-image', payload: { files } });
  }

  getBase64(file): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  isEmptyImageFigure(node) {
    if (!node?.attrs) {
      return false;
    }
    return node.attrs.type === 'figure' && (!node.attrs.src || node.attrs.src.length === 0);
  }

  zoom(file?: { id: string; url: string }) {
    const node = this.figureNode;
    if (!node?.attrs) {
      return;
    }
    this.dialog
      .open(FigurePreviewModalComponent, {
        data: {
          documentId: this.documentId,
          figureId: node.attrs.id,
          thumbnail: node.attrs.src,
          node,
          file,
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.emit({ id: 'set-selection' });
      });
  }

  isEmptyNativeTable(node) {
    if (!node?.attrs || !Array.isArray((node as any)?.content?.content)) {
      return false;
    }
    return (
      node.attrs.type === 'table' &&
      !node.attrs.src &&
      (node as any).content.content.find((inner) => inner.type.name === 'table') == null
    );
  }

  isImageFigure(node) {
    if (!node) {
      return false;
    }
    return !this.isEmpty(node) && !this.isActiveNativeTable(node);
  }

  isEmpty(node): boolean {
    // we neither have a (native table nor a table image)
    if (!node?.attrs) {
      return false;
    }
    return !(this.isActiveNativeTable(node) || node.attrs.src);
  }

  isActiveNativeTable(node) {
    if (!node?.attrs || !Array.isArray((node as any)?.content?.content)) {
      return false;
    }
    return (
      node.attrs.type === 'table' &&
      (node as any).content.content.find((inner) => inner.type.name === 'table') != null
    );
  }

  orientate(orientation: 'landscape' | 'portrait') {
    this.emit({ id: 'rotate' });
  }

  async openEditDialog() {
    if (this.mode === 'figure' && this.figureNode) {
      const figureNode = this.figureNode;
      try {
        const nodeSchema = await firstValueFrom(
          this.store
            .select(selectTemplate)
            .pipe(map((template) => template?.prosemirrorSchemas?.nodes)),
        );

        const dialogRef: MatDialogRef<NodeMetaEditorDialog> = this.dialog.open(
          NodeMetaEditorDialog,
          {
            data: {
              node: figureNode,
              schema: nodeSchema?.[SFNodeType.figure] || nodeMetaDataJsonSchema7?.[SFNodeType.figure],
              renderMode: 'simple',
            },
          },
        );

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.emit({
              id: 'update-alt-text',
              payload: result,
            });
          }
        });
      } catch (error) {
        console.error('Error in openEditDialog (figure mode):', error);
      }
      return;
    }

    if (this.mode === 'node' && this.figureNode) {
      const metadataNode = this.figureNode;
      try {
        const nodeType = metadataNode?.type?.name;
        const nodeSchema = await firstValueFrom(
          this.store
            .select(selectTemplate)
            .pipe(map((template) => template?.prosemirrorSchemas?.nodes)),
        );

        const dialogRef: MatDialogRef<NodeMetaEditorDialog> = this.dialog.open(
          NodeMetaEditorDialog,
          {
            data: {
              node: metadataNode,
              schema: nodeSchema?.[nodeType] ?? nodeMetaDataJsonSchema7?.[nodeType],
              renderMode: 'simple',
            },
          },
        );

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.emit({
              id: 'update-node',
              payload: result,
            });
          }
        });
      } catch (error) {
        console.error('Error in openEditDialog (node mode):', error);
      }
      return;
    }

    if (this.mode === 'mark' && this.markContext) {
      const markContext = this.markContext;
      try {
        const markType = markContext.type;
        const markSchema = await firstValueFrom(
          this.store
            .select(selectTemplate)
            .pipe(map((template) => template?.prosemirrorSchemas?.marks?.[markType] || null)),
        );

        const fakeNode: any = {
          attrs: { ...markContext.attrs },
          type: { name: markType },
        };

        const dialogRef: MatDialogRef<NodeMetaEditorDialog> = this.dialog.open(
          NodeMetaEditorDialog,
          {
            data: {
              node: fakeNode,
              schema: markSchema ?? {},
              renderMode: 'simple',
            },
          },
        );

        dialogRef.afterClosed().subscribe((result) => {
          if (!result || !this.editorView) {
            return;
          }

          const attrs = {
            ...markContext.attrs,
            ...result,
          };

          const id = attrs?.id ?? markContext.id ?? markContext.attrs?.id;
          if (id && attrs && !attrs.id) {
            attrs.id = id;
          }

          updateMarkAttrs(markType, attrs, {
            id,
            from: markContext.from,
            to: markContext.to,
          })(this.editorView.state, this.editorView.dispatch);
          this.markContext = {
            type: markType,
            attrs,
            id: markContext.id ?? attrs.id ?? null,
            from: markContext.from,
            to: markContext.to,
          };
        });
      } catch (error) {
        console.error('Error in openEditDialog (mark mode):', error);
      }
      return;
    }
  }

  ngOnInit(): void {}
}
